import {NavigationExtras, Router} from "@angular/router";
import {Observable, Subject, Subscription} from "rxjs";
import {Injectable} from "@angular/core";

@Injectable({
  providedIn: 'root'
})
export class EditingServiceProvider {
  private services = new Map<string, EditingService<any>>();

  constructor(private router: Router) {
  }

  getService<T>(key: string): EditingService<T> {
    if (!this.services.has(key)) {
      this.services.set(key, new EditingService<T>(this.router));
    }
    return this.services.get(key) as EditingService<T>;
  }
}

export class EditingService<T> {
  private registry: { [key: string]: EditingInstance<T> } = {};

  constructor(private router: Router) {
  }

  public createInstance(id: string, factory: () => T): EditingInstance<T> {
    let instance = new EditingInstance<T>(id, factory, this.router);
    this.registry[id] = instance;
    return instance;
  }

  public getInstance(Id: string): EditingInstance<T> | undefined {
    return this.registry[Id];
  }

  public getOrCreateInstance(id: string, factory: () => T): EditingInstance<T> {
    let instance = this.getInstance(id);
    if (!instance) {
      instance = this.createInstance(id, factory);
    }
    return instance;
  }

  public deleteInstance(Id: string): void {
    delete this.registry[Id];
  }
}

export class EditingInstanceBase {
  constructor(public Id: string) {
  }
}

export class EditingInstance<T> extends EditingInstanceBase {
  public subject: Subject<T> = new Subject<T>();
  public entity: T | undefined = undefined;
  public redirect: string | undefined = undefined;
  public extras?: NavigationExtras;
  public isEntryPoint: boolean = false;
  private subscriptions: Subscription[] = [];

  constructor(id: string, private factory: () => T, private router: Router) {
    super(id);
  }

  public edit(redirect: string, isEntryPoint: boolean = false, entity?: T, extras?: NavigationExtras): Observable<T> {
    this.entity = entity ?? this.factory();
    this.isEntryPoint = isEntryPoint;
    this.redirect = redirect;
    this.extras = extras;
    return this.subject.asObservable();
  }

  public save(): void {
    if (!this.entity) {
      return this.cancel();
    }

    this.subject.next(this.entity);
    this.finish(this.redirect);
  }

  public cancel(): void {
    this.subject.error(new Error("User cancelled the operation."));
    this.finish(this.redirect);
  }

  public subscribe<T>(observable: Observable<T>, callback: (value: T) => void, error?: (err: any) => void) {
    const sub = observable.subscribe({
      next: callback,
      error: error ?? (() => {
      })
    });
    this.subscriptions.push(sub);
  }

  private finish(redirect: string | undefined = undefined) {
    if (!redirect) this.reset();

    else this.router.navigate([redirect], (this.isEntryPoint ? {} : {queryParams: {edit_id: this.Id}})
    ).then(_ => {
      this.reset();
      if (this.extras) {
        this.extras.queryParamsHandling = "merge"
        this.router.navigate([], this.extras).then();
      }
    });
  }

  private reset(): void {
    this.entity = undefined;
    this.redirect = undefined;
    this.subject = new Subject<T>();
  }
}
